import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { baserowAPI, Formalite, FormalityType, Customer, Company, TABLES } from '../services/baserowAPI';
import { ETAPES } from '../constants/formaliteOptions';

interface AddFormaliteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formalite: Partial<Formalite>) => Promise<void>;
}

export const AddFormaliteModal: React.FC<AddFormaliteModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    field_7260: '', // customer
    field_7261: '', // company
    field_7262: '', // description
    field_7263: '', // formality_type
    field_7265: '', // progression
    field_7269: '', // deadline
    field_7270: '', // forced_deadline
    field_7271: '', // tax_deadline
    field_7272: '', // tax
    field_7274: '', // sender
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formalityTypes, setFormalityTypes] = useState<FormalityType[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [searchCustomers, setSearchCustomers] = useState('');
  const [searchCompanies, setSearchCompanies] = useState('');
  const [searchTypes, setSearchTypes] = useState('');

  // Options triées et filtrées pour les selects
  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      const nameA = a.field_7245 || '';
      const nameB = b.field_7245 || '';
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
    });
  }, [customers]);

  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      const nameA = a.field_7248 || '';
      const nameB = b.field_7248 || '';
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
    });
  }, [companies]);

  const sortedFormalityTypes = useMemo(() => {
    return [...formalityTypes].sort((a, b) => {
      const nameA = a.field_7266 || '';
      const nameB = b.field_7266 || '';
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
    });
  }, [formalityTypes]);

  const filteredCustomers = useMemo(() => {
    if (!searchCustomers) return sortedCustomers;
    const search = searchCustomers.toLowerCase();
    return sortedCustomers.filter(customer => 
      customer.field_7245?.toLowerCase().includes(search)
    );
  }, [sortedCustomers, searchCustomers]);

  const filteredCompanies = useMemo(() => {
    if (!searchCompanies) return sortedCompanies;
    const search = searchCompanies.toLowerCase();
    return sortedCompanies.filter(company => 
      company.field_7248?.toLowerCase().includes(search)
    );
  }, [sortedCompanies, searchCompanies]);

  const filteredFormalityTypes = useMemo(() => {
    if (!searchTypes) return sortedFormalityTypes;
    const search = searchTypes.toLowerCase();
    return sortedFormalityTypes.filter(type => 
      type.field_7266?.toLowerCase().includes(search)
    );
  }, [sortedFormalityTypes, searchTypes]);

  // Charger les données depuis Baserow
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        setSearchCustomers('');
        setSearchCompanies('');
        setSearchTypes('');
        
        // Charger en parallèle les types, clients et entreprises avec pagination complète
        const [types, customersData, companiesData] = await Promise.all([
          baserowAPI.getAllFormalityTypes(),
          baserowAPI.getAllClients(),
          baserowAPI.getAllCompanies(),
        ]);

        setFormalityTypes(types);
        setCustomers(customersData);
        setCompanies(companiesData);
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
      } finally {
        setLoadingData(false);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Préparer les données à envoyer
      const dataToSend: any = {};

      // Ajouter les champs optionnels s'ils ne sont pas vides
      // Pour les champs link_row (customer, company, formality_type), convertir en tableau avec ID numérique
      if (formData.field_7260) {
        // Customer - doit être un ID numérique
        const customerId = parseInt(formData.field_7260, 10);
        if (!isNaN(customerId)) {
          dataToSend.field_7260 = [customerId];
        }
      }
      if (formData.field_7261) {
        // Company - doit être un ID numérique
        const companyId = parseInt(formData.field_7261, 10);
        if (!isNaN(companyId)) {
          dataToSend.field_7261 = [companyId];
        }
      }
      if (formData.field_7262) dataToSend.field_7262 = formData.field_7262.trim();
      if (formData.field_7263) {
        // Formality Type - doit être un ID numérique
        const typeId = parseInt(formData.field_7263, 10);
        if (!isNaN(typeId)) {
          dataToSend.field_7263 = [typeId];
        }
      }
      if (formData.field_7265) dataToSend.field_7265 = formData.field_7265;
      if (formData.field_7269) dataToSend.field_7269 = formData.field_7269;
      if (formData.field_7270) dataToSend.field_7270 = formData.field_7270;
      if (formData.field_7271) dataToSend.field_7271 = formData.field_7271;
      if (formData.field_7272) dataToSend.field_7272 = formData.field_7272;
      if (formData.field_7274) dataToSend.field_7274 = formData.field_7274.trim();

      console.log('Données envoyées à Baserow:', dataToSend);

      await onSave(dataToSend);

      // Réinitialiser le formulaire
      setFormData({
        field_7260: '',
        field_7261: '',
        field_7262: '',
        field_7263: '',
        field_7265: '',
        field_7269: '',
        field_7270: '',
        field_7271: '',
        field_7272: '',
        field_7274: '',
      });

      onClose();
    } catch (err) {
      console.error('Erreur lors de la création:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la création de la formalité'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Nouvelle Formalité
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Client */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client
            </label>
            {loadingData ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                Chargement des clients...
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Rechercher un client..."
                  value={searchCustomers}
                  onChange={(e) => setSearchCustomers(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                />
                <select
                  name="field_7260"
                  value={formData.field_7260}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                  size={Math.min(filteredCustomers.length + 1, 10)}
                >
                  <option value="">Sélectionner un client (optionnel)</option>
                  {filteredCustomers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.field_7245}
                    </option>
                  ))}
                </select>
                {filteredCustomers.length === 0 && !searchCustomers && (
                  <p className="text-xs text-amber-600 mt-1">
                    ℹ️ Aucun client disponible. Créez-en un depuis le Dashboard.
                  </p>
                )}
                {filteredCustomers.length === 0 && searchCustomers && (
                  <p className="text-xs text-amber-600 mt-1">
                    ℹ️ Aucun client ne correspond à votre recherche.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Entreprise */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entreprise
            </label>
            {loadingData ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                Chargement des entreprises...
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Rechercher une entreprise..."
                  value={searchCompanies}
                  onChange={(e) => setSearchCompanies(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                />
                <select
                  name="field_7261"
                  value={formData.field_7261}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                  size={Math.min(filteredCompanies.length + 1, 10)}
                >
                  <option value="">Sélectionner une entreprise (optionnel)</option>
                  {filteredCompanies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.field_7248}
                    </option>
                  ))}
                </select>
                {filteredCompanies.length === 0 && !searchCompanies && (
                  <p className="text-xs text-amber-600 mt-1">
                    ℹ️ Aucune entreprise disponible. Créez-en une depuis le Dashboard.
                  </p>
                )}
                {filteredCompanies.length === 0 && searchCompanies && (
                  <p className="text-xs text-amber-600 mt-1">
                    ℹ️ Aucune entreprise ne correspond à votre recherche.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="field_7262"
              value={formData.field_7262}
              onChange={handleChange}
              placeholder="Détails supplémentaires..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          {/* Type de formalité */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de formalité
            </label>
            {loadingData ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                Chargement des types...
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Rechercher un type..."
                  value={searchTypes}
                  onChange={(e) => setSearchTypes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                />
                <select
                  name="field_7263"
                  value={formData.field_7263}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                  size={Math.min(filteredFormalityTypes.length + 1, 10)}
                >
                  <option value="">Sélectionner un type</option>
                  {filteredFormalityTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.field_7266}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          {/* Progression */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Progression / Étape
            </label>
            <select
              name="field_7265"
              value={formData.field_7265}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Sélectionner une étape</option>
              {ETAPES.map((etape) => (
                <option key={etape} value={etape}>
                  {etape}
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Échéance automatique
              </label>
              <input
                type="date"
                name="field_7269"
                value={formData.field_7269}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Échéance forcée
              </label>
              <input
                type="date"
                name="field_7270"
                value={formData.field_7270}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Échéance fiscale
              </label>
              <input
                type="date"
                name="field_7271"
                value={formData.field_7271}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
          </div>

          {/* Statut fiscal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut fiscal
            </label>
            <select
              name="field_7272"
              value={formData.field_7272}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Sélectionner un statut</option>
              <option value="3137">Envoyé</option>
              <option value="3138">Reçu</option>
            </select>
          </div>

          {/* Expéditeur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expéditeur
            </label>
            <input
              type="text"
              name="field_7274"
              value={formData.field_7274}
              onChange={handleChange}
              placeholder="Nom de l'expéditeur"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Création en cours...' : 'Créer la formalité'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
